import React from 'react';
import { 
  Typography, IconButton, Slider, Paper,
  Card, CardContent, Stack, Tooltip
} from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import VolumeDownIcon from '@mui/icons-material/VolumeDown';
import VolumeMuteIcon from '@mui/icons-material/VolumeMute';
import { useAudioPlayer } from '../../context/AudioPlayerContext';

const AudioPlayer = () => {
  const {
    currentTrack,
    isPlaying,
    duration,
    currentTime,
    volume,
    togglePlay,
    handleSeek,
    handleVolumeChange
  } = useAudioPlayer();

  // Format time for display (mm:ss)
  const formatTime = (time) => {
    if (isNaN(time)) return '00:00';
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  };

  // Volume icon based on level
  const getVolumeIcon = () => {
    if (volume === 0) return <VolumeMuteIcon fontSize="small" />;
    if (volume < 0.5) return <VolumeDownIcon fontSize="small" />;
    return <VolumeUpIcon fontSize="small" />;
  };

  if (!currentTrack) {
    return (
      <Paper 
        elevation={1} 
        sx={{ 
          p: 2, 
          mt: 2,
          borderRadius: 2,
          background: 'rgba(0,0,0,0.02)',
          textAlign: 'center' 
        }}
      >
        <Typography variant="body2" color="text.secondary">
          No audio playing
        </Typography>
      </Paper>
    );
  }

  return (
    <Card 
      elevation={1}
      sx={{
        mt: 2,
        borderRadius: 2,
        overflow: 'visible'
      }}
    >
      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
        <Typography variant="subtitle2" noWrap>
          {currentTrack.title || 'Unknown Track'}
        </Typography>
        
        {currentTrack.artist && (
          <Typography variant="caption" color="text.secondary" noWrap>
            {currentTrack.artist}
          </Typography>
        )}
        
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1 }}>
          <IconButton 
            onClick={togglePlay} 
            color="primary" 
            size="small"
          >
            {isPlaying ? <PauseIcon /> : <PlayArrowIcon />}
          </IconButton>
          
          <Typography variant="caption" sx={{ width: 40 }}>
            {formatTime(currentTime)}
          </Typography>
          
          <Slider
            value={currentTime}
            max={duration || 100}
            onChange={(_, value) => handleSeek(value)}
            size="small"
            sx={{ mx: 1 }}
          />
          
          <Typography variant="caption" sx={{ width: 40 }}>
            {formatTime(duration)}
          </Typography>
        </Stack>
        
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1 }}>
          <Tooltip title="Volume">
            <IconButton size="small" onClick={() => handleVolumeChange(volume === 0 ? 0.5 : 0)}>
              {getVolumeIcon()}
            </IconButton>
          </Tooltip>
          
          <Slider
            value={volume}
            min={0}
            max={1}
            step={0.01}
            onChange={(_, value) => handleVolumeChange(value)}
            size="small"
            aria-label="Volume"
            sx={{ mx: 1 }}
          />
        </Stack>
      </CardContent>
    </Card>
  );
};

export default AudioPlayer;
